import React from 'react';

export interface BannerClassNames {
  container: string;
  contentAligner: string;
  maxWidthContainer: string;
  contentWrapper: string;
  layoutRow: string;
  leftFeatureIconWrapper: string;
  titleSection: string;
  titleContainer: string;
  title: string;
  rightActionWrapper: string;
  rightActionButton: string;
  subtitleContainer: string;
  subtitleOne: string;
  subtitleTwo: string;
}

interface BannerProps {
  leftFeatureIcon: React.ReactNode;
  title: React.ReactNode;
  subtitleOne?: React.ReactNode;
  subtitleTwo?: React.ReactNode;
  rightActionIcon?: React.ReactNode;
  onRightActionClick: () => void;
  rightActionAriaLabel?: string;
  classNames?: Partial<BannerClassNames>;
}

const defaultClassNames: BannerClassNames = {
  container: 'm-0 w-full -mt-5 bg-light-blue pb-5 pt-8 md:-mt-px md:h-[230px] md:pt-2.5',
  contentAligner: 'flex w-full items-center justify-center',
  maxWidthContainer: 'w-full max-w-[380px] sm:max-w-[540px] md:max-w-[868px] lg:max-w-[1320px]',
  contentWrapper: '-mt-5 bg-light-blue px-5 pt-[30px] md:-mt-[5px]',
  layoutRow: 'flex w-full flex-row',
  leftFeatureIconWrapper: 'hidden justify-center sm:w-1/12 md:inline-flex md:h-[82px] md:items-center md:self-start',
  titleSection: 'w-full sm:w-11/12',
  titleContainer: 'w-full text-left text-white',
  title: 'mb-[15px] w-full font-bold leading-6 text-[1.475rem] md:w-4/5 md:text-[1.875rem] md:leading-[2.35rem]',
  rightActionWrapper: 'flex w-1/4 items-center justify-center sm:w-1/6 lg:w-1/12',
  rightActionButton:
    'mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full border-3 border-white bg-transparent p-0 hover:bg-transparent hover:shadow-lg transition-shadow',
  subtitleContainer: 'mt-[15px] w-full text-left md:w-1/2',
  subtitleOne: 'text-left',
  subtitleTwo: 'text-left',
};

const mergeClassNames = (defaultClassName: string, overrideClassName?: string) =>
  overrideClassName ? `${defaultClassName} ${overrideClassName}` : defaultClassName;

export const HomeGlyph = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox='0 0 24 24' fill='none' aria-hidden='true'>
    <path d='M3 10.75L12 3l9 7.75' fill='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M6.75 9.75V21h10.5V9.75' fill='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const Banner = ({
  leftFeatureIcon,
  title,
  subtitleOne,
  subtitleTwo,
  rightActionIcon = <HomeGlyph />,
  onRightActionClick,
  rightActionAriaLabel = 'banner action',
  classNames = {},
}: BannerProps) => {
  return (
    <div className={mergeClassNames(defaultClassNames.container, classNames.container)}>
      <div className={mergeClassNames(defaultClassNames.contentAligner, classNames.contentAligner)}>
        <div className={mergeClassNames(defaultClassNames.maxWidthContainer, classNames.maxWidthContainer)}>
          <div className={mergeClassNames(defaultClassNames.contentWrapper, classNames.contentWrapper)}>
            <div className={mergeClassNames(defaultClassNames.layoutRow, classNames.layoutRow)}>
              <div className={mergeClassNames(defaultClassNames.leftFeatureIconWrapper, classNames.leftFeatureIconWrapper)}>{leftFeatureIcon}</div>
              <div className={mergeClassNames(defaultClassNames.titleSection, classNames.titleSection)}>
                <div className='flex w-full flex-wrap'>
                  <div className='w-3/4 sm:w-5/6 lg:w-11/12'>
                    <div className='flex w-full justify-between'>
                      <div className={mergeClassNames(defaultClassNames.titleContainer, classNames.titleContainer)}>
                        <h1 className={mergeClassNames(defaultClassNames.title, classNames.title)}>{title}</h1>
                      </div>
                    </div>
                  </div>
                  <div className={mergeClassNames(defaultClassNames.rightActionWrapper, classNames.rightActionWrapper)}>
                    <button
                      type='button'
                      className={mergeClassNames(defaultClassNames.rightActionButton, classNames.rightActionButton)}
                      aria-label={rightActionAriaLabel}
                      onClick={onRightActionClick}
                    >
                      {rightActionIcon}
                    </button>
                  </div>
                  {(subtitleOne || subtitleTwo) && (
                    <div className={mergeClassNames(defaultClassNames.subtitleContainer, classNames.subtitleContainer)}>
                      {subtitleOne && <div className={mergeClassNames(defaultClassNames.subtitleOne, classNames.subtitleOne)}>{subtitleOne}</div>}
                      {subtitleTwo && <div className={mergeClassNames(defaultClassNames.subtitleTwo, classNames.subtitleTwo)}>{subtitleTwo}</div>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Banner;
